import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  XMarkIcon,
  ClipboardDocumentListIcon,
  TagIcon,
  DocumentTextIcon,
  UserIcon,
  PaperClipIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useSnackbar } from "../../../context/SnackbarContext";
import { Button } from "../../ui";
import { IMPL_STATUSES, EMPTY_CTRL } from "./constants";
import { getRiskMasterPickerApi } from "../../../services/axios/AuthApis/TrustCenterApis";
import ApiLoader from "../../ui/ApiLoader";
import { DEPARTMENTS_WITH_DESIGNATIONS, CONTROL_CATEGORIES } from "../../../constants/AppConstants";
import { useAuth } from "../../../context/AuthContext";

const ControlFormModal = ({ open, onClose, initial, onSave, sourceType = "framework-master" }) => {
  const [form, setForm] = useState(EMPTY_CTRL);
  const [saving, setSaving] = useState(false);
  const { success, error } = useSnackbar();
  const user = useAuth();
  const isCustomerAdmin =  user?.user?.role === "customer_admin" ||
    user?.user?.role === "security_analyst";
  console.log({ user,isCustomerAdmin, initial });
  /* ── Risk picker state ── */
  const [risks, setRisks] = useState([]);
  const [risksLoading, setRisksLoading] = useState(false);
  const [riskSearch, setRiskSearch] = useState('');
  const [riskMetaMap, setRiskMetaMap] = useState(() => {
    const map = {};
    if (initial?.risk_ids && initial?.risk_labels) {
      initial.risk_ids.forEach((rid, i) => {
        const label = initial.risk_labels[i] || '';
        const sepIdx = label.indexOf(' — ');
        const risk_id = sepIdx > -1 ? label.substring(0, sepIdx) : label;
        const risk_description = sepIdx > -1 ? label.substring(sepIdx + 3) : '';
        map[rid] = { risk_id, risk_description, category: '' };
      });
    }
    return map;
  });

  /* ── Category multi-select state ── */
  const [categorySearch, setCategorySearch] = useState('');

  const filteredCategories = CONTROL_CATEGORIES.filter((cat) => {
    if (!categorySearch.trim()) return true;
    return cat.toLowerCase().includes(categorySearch.toLowerCase());
  });

  const toggleCategory = (cat) => {
    setForm((f) => {
      const cats = f.category.includes(cat)
        ? f.category.filter((c) => c !== cat)
        : [...f.category, cat];
      return { ...f, category: cats };
    });
  };

  /* ── Owner multi-select state ── */
  const [ownerSearch, setOwnerSearch] = useState('');

  const allOwnerRoles = DEPARTMENTS_WITH_DESIGNATIONS.flatMap((dept) =>
    dept.roles.map((r) => ({ key: r.role, role: r.role, department: dept.name, responsibility: r.responsibility }))
  );

  const filteredOwnerRoles = allOwnerRoles.filter((r) => {
    if (!ownerSearch.trim()) return true;
    const q = ownerSearch.toLowerCase();
    return r.role.toLowerCase().includes(q) || r.department.toLowerCase().includes(q);
  });

  const toggleOwner = (role) => {
    setForm((f) => {
      const owners = f.control_owner.includes(role)
        ? f.control_owner.filter((o) => o !== role)
        : [...f.control_owner, role];
      return { ...f, control_owner: owners };
    });
  };

  useEffect(() => {
    if (open) {
      setForm((prev) => {
        // Only reset if opening for the first time or initial.id changes
        if (!prev.id && initial) {
          const init = { ...EMPTY_CTRL, ...initial };
          if (typeof init.control_owner === 'string') {
            init.control_owner = init.control_owner ? init.control_owner.split(' / ') : [];
          }
          if (typeof init.category === 'string') {
            init.category = init.category ? init.category.split(', ') : [];
          }
          return init;
        }
        if (!initial) {
          return { ...EMPTY_CTRL };
        }
        return prev;
      });
      // Re-seed riskMetaMap from initial data on open
      if (initial?.risk_ids && initial?.risk_labels) {
        const map = {};
        initial.risk_ids.forEach((rid, i) => {
          const label = initial.risk_labels[i] || '';
          const sepIdx = label.indexOf(' — ');
          const risk_id = sepIdx > -1 ? label.substring(0, sepIdx) : label;
          const risk_description = sepIdx > -1 ? label.substring(sepIdx + 3) : '';
          map[rid] = { risk_id, risk_description, category: '' };
        });
        setRiskMetaMap(map);
      } else {
        setRiskMetaMap({});
      }
    }
  }, [open, initial?.id]);

  /* ── Load risks for the picker ── */
  const loadRisks = useCallback(async () => {
    setRisksLoading(true);
    try {
      const data = await getRiskMasterPickerApi('', 1, 500);
      const list = data.risks || [];
      setRisks(list);
      setRiskMetaMap((prev) => {
        const next = { ...prev };
        list.forEach((r) => {
          next[r.id] = { risk_id: r.risk_id, risk_description: r.risk_description, category: r.category };
        });
        return next;
      });
    } catch {
      setRisks([]);
    } finally {
      setRisksLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadRisks();
  }, [open, loadRisks]);

  const isEdit = Boolean(initial?.id);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  /* ── Risk toggle helpers ── */
  const toggleRisk = (riskId) => {
    setForm((f) => {
      const ids = f.risk_ids.includes(riskId)
        ? f.risk_ids.filter((id) => id !== riskId)
        : [...f.risk_ids, riskId];
      return { ...f, risk_ids: ids };
    });
  };

  const filteredRisks = risks.filter((r) => {
    if (!riskSearch.trim()) return true;
    const q = riskSearch.toLowerCase();
    return (
      (r.risk_id || '').toLowerCase().includes(q) ||
      (r.risk_description || '').toLowerCase().includes(q) ||
      (r.category || '').toLowerCase().includes(q)
    );
  });

  const selectAllFilteredRisks = () => {
    const filtered = filteredRisks.map((r) => r.id);
    setForm((f) => ({
      ...f,
      risk_ids: [...new Set([...f.risk_ids, ...filtered])],
    }));
  };

  const deselectAllFilteredRisks = () => {
    const filtered = new Set(filteredRisks.map((r) => r.id));
    setForm((f) => ({
      ...f,
      risk_ids: f.risk_ids.filter((id) => !filtered.has(id)),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.control_id.trim()) return error("Control ID is required");
    if (!form.title.trim())      return error("Title is required");
    setSaving(true);
    try {
      await onSave({ ...form, control_owner: form.control_owner.join(' / '), category: form.category.join(', ') });
      success(`Control ${form.id ? 'updated' : 'created'}!`);
      onClose();
    } catch (err) {
      error(err?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, required, icon: Icon, children }) => (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
        )}
        {children}
      </div>
    </div>
  );

  const inputCls = (hasIcon = true) =>
    `cfm-input w-full ${hasIcon ? "pl-9" : "pl-3"} pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-colors bg-gray-50 focus:bg-white`;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <style>{`
            .cfm-multiselect:focus-within {
              border-color: var(--app-primary-color, #26256b) !important;
              box-shadow: 0 0 0 2px color-mix(in srgb, var(--app-primary-color, #26256b) 20%, transparent);
            }
            .cfm-multiselect:focus-within .cfm-search-icon {
              color: var(--app-primary-color, #26256b);
            }
          `}</style>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            key="modal"
            initial={{ opacity: 0, y: -24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center gap-4 px-6 py-5 rounded-t-2xl" style={{ background: "var(--app-primary-color, #26256b)" }}>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-white/20 rounded-xl">
                <ClipboardDocumentListIcon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-white leading-tight">
                  {isEdit ? "Edit Control" : "New Control"}
                </h3>
                <p className="text-xs text-white/70 mt-0.5">
                  {isEdit
                    ? "Update the control details below"
                    : "Define a new control for this framework"}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex-shrink-0 p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <form id="ctrl-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto relative">
              <div className="px-6 py-5 space-y-5">
                {saving && (
                  <ApiLoader variant="overlay" label="Saving..." />
                )}

                {/* Section: Identity */}
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Identity</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Control ID" required icon={ClipboardDocumentListIcon}>
                      <input
                        type="text"
                        name="control_id"
                        value={form.control_id}
                        onChange={handleChange}
                        placeholder="e.g. A.5.1"
                        className={inputCls()}
                      />
                    </Field>
                  </div>
                  {isCustomerAdmin && (
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                        Customer Settings
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                            renewal date: {form.renewal_date || "Not set"}
                          </span> 
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                            Renewal Period
                          </label>
                          <select
                            name="renewal_period"
                            value={form.renewal_period}
                            onChange={handleChange}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white"
                          >
                            <option value="">Select</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="bi-annually">Bi-Annually</option>
                            <option value="annually">Annually</option>
                          </select>
                        </div>

                        {/* Is Applicable Toggle */}
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl border border-gray-200">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Is Applicable</p>
                            <p className="text-xs text-gray-500">
                              Mark whether this control applies to the customer
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                is_applicable: !prev.is_applicable,
                              }))
                            }
                            className={`relative inline-flex h-6 w-11 rounded-full transition ${
                              form.is_applicable ? "bg-green-600" : "bg-gray-300"
                            }`}
                          >
                            <span
                              className={`inline-block h-5 w-5 bg-white rounded-full transform transition ${
                                form.is_applicable ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Category multi-select */}
                  <div className="mt-4">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      Category{' '}
                      <span className="text-gray-400 font-normal">({form.category.length} selected)</span>
                    </label>

                    {/* Selected category chips */}
                    {form.category.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {form.category.map((cat) => (
                          <span
                            key={cat}
                            className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-2.5 py-1"
                          >
                            <span className="font-semibold">{cat}</span>
                            {!isCustomerAdmin && (
                              <button
                                type="button"
                                onClick={() => toggleCategory(cat)}
                                className="ml-0.5 text-purple-400 hover:text-red-500 transition-colors"
                              >
                                <XMarkIcon className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                    {!isCustomerAdmin &&(
                    <div className="cfm-multiselect border border-gray-200 rounded-lg overflow-hidden transition-all">
                      {/* Search */}
                      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                        <MagnifyingGlassIcon className="cfm-search-icon w-4 h-4 text-gray-400 shrink-0 transition-colors" />
                        <input
                          type="text"
                          placeholder="Search categories…"
                          value={categorySearch}
                          onChange={(e) => setCategorySearch(e.target.value)}
                          className="flex-1 text-xs bg-transparent outline-none text-gray-800 placeholder-gray-400"
                          style={{ caretColor: "var(--app-primary-color, #26256b)" }}
                        />
                      </div>
                      {/* Scrollable list */}
                      <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
                        {filteredCategories.length === 0 ? (
                          <p className="text-xs text-gray-400 py-3 text-center">No matching categories.</p>
                        ) : (
                          filteredCategories.map((cat) => {
                            const checked = form.category.includes(cat);
                            return (
                              <label
                                key={cat}
                                className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-purple-50 transition-colors ${
                                  checked ? 'bg-purple-50/60' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleCategory(cat)}
                                  className="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                />
                                <span className="text-xs font-medium text-gray-800">{cat}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                    )}
                  </div>
                </div>

                {/* Section: Details */}
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Details</p>
                  <div className="space-y-4">
                    <Field label="Title"  required = {!isCustomerAdmin} icon={DocumentTextIcon}>
                      <input
                        type="text"
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        placeholder="Control title"
                        className={inputCls()}
                        disabled={isCustomerAdmin}
                      />
                    </Field>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Description
                      </label>
                      <div className="relative">
                        <DocumentTextIcon className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                        <textarea
                          name="description"
                          rows={3}
                          value={form.description}
                          onChange={handleChange}
                          disabled={isCustomerAdmin}
                          placeholder="Describe the control requirement and expected outcome…"
                          className="cfm-input w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-colors resize-none bg-gray-50 focus:bg-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section: Ownership & Evidence */}
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Ownership &amp; Evidence</p>

                  {/* Evidence Type */}
                  <div className="mb-4">
                    <Field label="Evidence Type" icon={PaperClipIcon}>
                      <input
                        type="text"
                        name="evidence_type"
                        value={form.evidence_type}
                        onChange={handleChange}
                        disabled={isCustomerAdmin}
                        placeholder="e.g. Policy Document"
                        className={inputCls()}
                      />
                    </Field>
                  </div>

                  {/* Control Owner multi-select */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      Control Owner{' '}
                      <span className="text-gray-400 font-normal">({form.control_owner.length} selected)</span>
                    </label>

                    {/* Selected owner chips */}
                    {form.control_owner.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {form.control_owner.map((role) => (
                          <span
                            key={role}
                            className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2.5 py-1"
                          >
                            <span className="font-semibold">{role}</span>
                            {!isCustomerAdmin && (
                              <button
                                type="button"
                                onClick={() => toggleOwner(role)}
                                className="ml-0.5 text-green-400 hover:text-red-500 transition-colors"
                              >
                              <XMarkIcon className="w-3.5 h-3.5" />
                            </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                    {!isCustomerAdmin && (
                      <div className="cfm-multiselect border border-gray-200 rounded-lg overflow-hidden transition-all">
                        {/* Search */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                          <MagnifyingGlassIcon className="cfm-search-icon w-4 h-4 text-gray-400 shrink-0 transition-colors" />
                          <input
                            type="text"
                            placeholder="Search departments or roles…"
                            value={ownerSearch}
                            onChange={(e) => setOwnerSearch(e.target.value)}
                            className="flex-1 text-xs bg-transparent outline-none text-gray-800 placeholder-gray-400"
                            style={{ caretColor: "var(--app-primary-color, #26256b)" }}
                          />
                        </div>
                        {/* Scrollable list */}
                          <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
                            {filteredOwnerRoles.length === 0 ? (
                              <p className="text-xs text-gray-400 py-3 text-center">No matching roles.</p>
                            ) : (
                              filteredOwnerRoles.map((r) => {
                              const checked = form.control_owner.includes(r.role);
                              return (
                                <label
                                  key={`${r.department}-${r.role}`}
                                  className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-green-50 transition-colors ${
                                    checked ? 'bg-green-50/60' : ''
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleOwner(r.role)}
                                    className="mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                  />
                                  <div className="min-w-0">
                                    <span className="text-xs font-semibold text-gray-800">{r.role}</span>
                                    <span className="ml-2 text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                      {r.department}
                                    </span>
                                  </div>
                                </label>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Section: Risk & Status */}
                {/* Platform Admin (framework-master): Implementation Status is
                    the ControlMaster template default - not shown here, it is
                    only relevant on the live customer control below. */}
                {sourceType === "customer-framework" && (
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Risk &amp; Status</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                          Implementation Status
                        </label>
                        <div className="relative">
                          <ExclamationTriangleIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          <select
                            name="implementation_status"
                            value={form.implementation_status}
                            onChange={handleChange}
                            className="cfm-input w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none transition-colors appearance-none bg-gray-50 focus:bg-white"
                          >
                            {IMPL_STATUSES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Auto-updates from the control's uploaded/approved files - not manually editable.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section: Map Risks */}
                {!isCustomerAdmin && (
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                      Map Risks{' '}
                      <span className="text-gray-300 font-normal normal-case">
                        ({form.risk_ids.length} selected)
                      </span>
                    </p>

                    {risksLoading ? (
                      <div className="flex items-center gap-2 text-xs text-gray-500 py-3">
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        Loading risks…
                      </div>
                    ) : risks.length === 0 ? (
                      <p className="text-xs text-gray-400 py-2">No risks available in Risk Master.</p>
                    ) : (
                      <div className="cfm-multiselect border border-gray-200 rounded-lg overflow-hidden transition-all">
                        {/* Search & bulk actions */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                          <MagnifyingGlassIcon className="cfm-search-icon w-4 h-4 text-gray-400 shrink-0 transition-colors" />
                          <input
                            type="text"
                            placeholder="Search risks…"
                            value={riskSearch}
                            onChange={(e) => setRiskSearch(e.target.value)}
                            className="flex-1 text-xs bg-transparent outline-none text-gray-800 placeholder-gray-400"
                            style={{ caretColor: "var(--app-primary-color, #26256b)" }}
                          />
                          <button
                            type="button"
                            onClick={selectAllFilteredRisks}
                            className="text-[10px] hover:underline whitespace-nowrap"
                            style={{ color: "var(--app-primary-color, #26256b)" }}
                          >
                            Select all
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            type="button"
                            onClick={deselectAllFilteredRisks}
                            className="text-[10px] text-red-500 hover:underline whitespace-nowrap"
                          >
                            Deselect all
                          </button>
                        </div>
                        {/* Scrollable list */}
                        <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                          {filteredRisks.length === 0 ? (
                            <p className="text-xs text-gray-400 py-3 text-center">No matching risks.</p>
                          ) : (
                            filteredRisks.map((r) => {
                              const checked = form.risk_ids.includes(r.id);
                              return (
                                <label
                                  key={r.id}
                                  className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-orange-50 transition-colors ${
                                    checked ? 'bg-orange-50/60' : ''
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleRisk(r.id)}
                                    className="mt-0.5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                                  />
                                  <div className="min-w-0">
                                    <span className="text-xs font-semibold text-gray-800">
                                      {r.risk_id}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-1.5">— {r.risk_description}</span>
                                    {r.category && (
                                      <span className="ml-2 text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                                        {r.category}
                                      </span>
                                    )}
                                  </div>
                                </label>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}

                    {/* Selected Risks Summary */}
                    {form.risk_ids.length > 0 && (
                      <div className="mt-3">
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                          Selected Risks{' '}
                          <span className="text-gray-400 font-normal">({form.risk_ids.length})</span>
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                          {form.risk_ids.map((rid) => {
                            const risk = risks.find((r) => r.id === rid) || riskMetaMap[rid];
                            return (
                              <span
                                key={rid}
                                className="inline-flex items-center gap-1 text-xs bg-orange-50 text-orange-700 border border-orange-200 rounded-full px-2.5 py-1"
                              >
                                <span className="font-semibold">{risk?.risk_id || rid}</span>
                                {risk?.risk_description && (
                                  <span className="text-orange-500 max-w-[160px] truncate">— {risk.risk_description}</span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => toggleRisk(rid)}
                                  className="ml-0.5 text-orange-400 hover:text-red-500 transition-colors"
                                >
                                  <XMarkIcon className="w-3.5 h-3.5" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Active toggle */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${form.is_active ? "bg-green-100" : "bg-gray-200"}`}>
                      <CheckCircleIcon className={`w-4 h-4 ${form.is_active ? "text-green-600" : "text-gray-400"}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Active Status</p>
                      <p className="text-xs text-gray-500">
                        {form.is_active ? "Control is active and enforced" : "Control is disabled"}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.is_active}
                    onClick={() => setForm((prev) => ({ ...prev, is_active: !prev.is_active }))}
                    className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none"
                    style={{ backgroundColor: form.is_active ? "var(--app-primary-color, #26256b)" : "#D1D5DB" }}
                    disabled={isCustomerAdmin}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg transform ring-0 transition duration-200 ease-in-out ${
                        form.is_active ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

              </div>
            </form>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/80 rounded-b-2xl">
              <p className="text-xs text-gray-400">
                <span className="text-red-400">*</span> Required fields
              </p>
              <div className="flex items-center gap-3">
                <Button type="button" variant="secondary" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="ctrl-form"
                  variant="primary"
                  size="sm"
                  disabled={saving}
                  loading={saving}
                >
                  {saving ? "Saving…" : isEdit ? "Update Control" : "Create Control"}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ControlFormModal;
